import React, { useState, useEffect, useMemo } from "react";
import { Table, Select, DatePicker, Input, Upload, Button, Tooltip, Typography, Tag, message } from "antd";
import {
  CheckCircleTwoTone, CloseCircleTwoTone, EditOutlined, DownOutlined,
  CalendarOutlined, UploadOutlined, CloseCircleFilled, DeleteOutlined, PictureOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  FIELD_DEFS, FIELD_TYPE, DOWNSTREAM_FIELDS, optionsForField,
  dateBoundsForField, datesInvalidatedBy,
  missingParentsFor, parentScopeSummary, labelsFor, joinLabels, PARENT_FIELDS,
  FIELD_DEF_BY_FIELD, requiredFieldSet, requirementFor, REQUIREMENT,
} from "./fieldDefs";
import { FILE_NAME_FIELDS } from "./matchFilesToRows";

const { Text } = Typography;
const { Option } = Select;

export const cellKey = (rowId, field) => `${rowId}::${field}`;
export const cellDomId = (rowId, field) => `hob-cell-${rowId}-${field}`;

const ERROR_BORDER = "1px solid #ff4d4f";
const DIRTY_BORDER = "1px dashed #faad14";

// Day granularity only — these are plain dates with no time component, so
// both sides truncate to the calendar day before comparing.
const isBeforeDay = (a, b) => moment(a).isBefore(moment(b), "day");

/**
 * What an empty dropdown says. "Fill in the columns this one depends on
 * first" was both vague and often untrue — a scoped list can come back empty
 * with every parent already filled, and then the real answer is that nothing
 * exists for that combination, which is a different problem needing a
 * different fix. Name the actual columns in the first case, and the actual
 * combination in the second.
 */
function emptyListMessage(def, row, options, masterData) {
  if (options.length) return "No match";

  const missing = missingParentsFor(def.field, row);
  if (missing.length) {
    return `Fill in ${joinLabels(labelsFor(missing))} first`;
  }

  const scope = parentScopeSummary(def.field, row);
  if (!scope) return `No ${def.label} options available`;

  // Every parent is filled and the list is still empty, so at least one of
  // them is over-constraining the row. Work out, per parent, which of its
  // values would actually yield something — then advise on whichever has the
  // fewest, since that's the narrowest, most concrete fix. (Okra exists only
  // as "Loose": changing Sample Variant has exactly one answer, while
  // changing Commodity has a dozen, so Sample Variant is the better advice
  // even though Commodity is equally "a" blocker.)
  const candidates = (PARENT_FIELDS[def.field] || [])
    .map((parent) => {
      const parentDef = FIELD_DEF_BY_FIELD[parent];
      if (!parentDef) return null;
      const workable = optionsForField(parentDef, { ...row, [parent]: "" }, masterData)
        .filter((value) =>
          optionsForField(def, { ...row, [parent]: value }, masterData).length > 0);
      return workable.length ? { parentDef, workable } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.workable.length - b.workable.length);

  if (!candidates.length) return `No ${def.label} exists for ${scope}`;

  const best = candidates[0];
  return best.workable.length <= 3
    ? `No ${def.label} for ${scope} — set ${best.parentDef.label} to ${best.workable.join(" or ")}`
    : `No ${def.label} for ${scope} — check ${best.parentDef.label}`;
}

const REQUIRED_MARK = "#ff4d4f";
const CONDITIONAL_MARK = "#faad14";
const OPTIONAL_LABEL = "#8c8c8c";

/**
 * A column header that says whether the column has to be filled in. With ~65
 * columns, the only thing anyone could previously tell from the header row
 * was the column's name — which of them a row would actually be rejected for
 * leaving blank was invisible until Re-validate came back with a list of
 * "is required" errors, one per blank cell per row.
 *
 * Required gets the red asterisk the rest of the app's forms use, optional is
 * greyed rather than left simply unmarked (an unmarked column reads as easily
 * like an oversight as a decision), and Completed Date gets its own amber
 * mark because it's required in one state and forbidden in the other, so a
 * flat asterisk would be wrong half the time. Hovering any header spells the
 * same thing out in words.
 */
function ColumnHeader({ def, requirement }) {
  const mark = requirement.level === REQUIREMENT.REQUIRED
    ? REQUIRED_MARK
    : requirement.level === REQUIREMENT.CONDITIONAL
      ? CONDITIONAL_MARK
      : null;
  return (
    <Tooltip title={requirement.note}>
      <span style={{ color: mark ? undefined : OPTIONAL_LABEL }}>
        {def.label}
        {mark && <span style={{ color: mark, marginLeft: 3 }}>*</span>}
      </span>
    </Tooltip>
  );
}

/**
 * One grid cell. It renders as plain text until clicked, and only then swaps
 * in the real control — with ~50 columns per row, always-mounted inputs make
 * the table crawl, and this keeps the DOM light while still being editable
 * everywhere.
 *
 * The affordance also says what kind of cell it is at a glance, which is the
 * point: a caret for anything picked from a list, a pencil for anything
 * typed by hand, a calendar for dates.
 */
function GridCell({ def, row, rows = [], masterData, pdfFiles, errors, dirty, focused, onChange }) {
  const [editing, setEditing] = useState(false);
  const value = row[def.field];

  // Real-time direct client validation for completedDate vs targetEndStatus
  const directErrors = useMemo(() => {
    const list = [...(errors || [])];
    if (def.field === "completedDate") {
      const target = String(row.targetEndStatus || "").trim().toLowerCase();
      const hasDate = Boolean(String(row.completedDate || "").trim());
      if (target === "report uploaded" && hasDate) {
        if (!list.some((e) => e.field === "completedDate")) {
          list.push({ field: "completedDate", message: 'must be blank unless Target End Status is "Completed"' });
        }
      } else if (target === "completed" && !hasDate) {
        if (!list.some((e) => e.field === "completedDate")) {
          list.push({ field: "completedDate", message: 'is required when Target End Status is "Completed"' });
        }
      }
    }
    return list;
  }, [def.field, row.targetEndStatus, row.completedDate, errors]);

  const hasError = directErrors.length > 0;
  const showRed = hasError;
  const showAmber = dirty && !hasError;

  const wrapperStyle = {
    minHeight: 30,
    padding: "3px 6px",
    borderRadius: 4,
    cursor: def.type === FIELD_TYPE.FILE ? "default" : "pointer",
    border: showRed ? ERROR_BORDER : showAmber ? DIRTY_BORDER : "1px solid transparent",
    background: showRed ? "#fff1f0" : showAmber ? "#fffbe6" : "transparent",
    boxShadow: focused ? "0 0 0 2px #1890ff" : undefined,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  };

  if (def.type === FIELD_TYPE.FILE) {
    const file = row[def.field];
    const isPdfField = def.field === "reportFile" || def.field === "reportMetadataFile";
    const nameField = FILE_NAME_FIELDS[def.field];
    const expectedName = nameField ? String(row[nameField] || "").trim() : "";
    const hasAttached = Boolean(file);

    const handleClearFile = (e) => {
      if (e) e.stopPropagation();
      onChange(null);
      if (nameField && row[nameField]) {
        row[nameField] = "";
      }
    };

    const uploadProps = {
      showUploadList: false,
      accept: isPdfField
        ? ".pdf,application/pdf"
        : "image/*,.jpg,.jpeg,.png,.webp,.bmp,.gif,.svg",
      beforeUpload: (f) => {
        if (f.size === 0) {
          message.error("The selected file is empty (0 bytes). Please upload a valid file.");
          return false;
        }
        if (isPdfField) {
          const isPdf = f.type === "application/pdf" || /\.(pdf)$/i.test(f.name);
          if (!isPdf) {
            message.error(`"${f.name}" is not a PDF file. Only .pdf files are allowed for report/metadata documents.`);
            return false;
          }
        } else {
          const isImage = (f.type && f.type.startsWith("image/")) || /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(f.name);
          if (!isImage) {
            message.error(`"${f.name}" is not a valid image. Only image files (JPG, PNG, WEBP, etc.) are allowed for photo columns.`);
            return false;
          }
        }
        onChange(f);
        return false;
      },
    };

    return (
      <div id={cellDomId(row.rowId, def.field)} style={wrapperStyle}>
        <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          {isPdfField && pdfFiles && pdfFiles.length > 0 ? (
            <Select
              size="small"
              dropdownMatchSelectWidth={false}
              value={file ? file.name : undefined}
              placeholder="Select PDF..."
              showSearch
              allowClear
              style={{ width: "100%", fontSize: 11 }}
              onChange={(fileName) => {
                if (!fileName) {
                  handleClearFile();
                  return;
                }
                const picked = pdfFiles.find((f) => f.name === fileName);
                if (picked) onChange(picked);
              }}
              filterOption={(input, option) => {
                const labelText = typeof option.children === "string" ? option.children : String(option.value || "");
                return labelText.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {pdfFiles.map((pdf) => {
                const assignedTarget = rows.reduce((found, r, rIdx) => {
                  if (found) return found;
                  // Check reportFile
                  if (r.reportFile && r.reportFile.name === pdf.name) {
                    const isThisExactCell = (r.rowId === row.rowId && def.field === "reportFile");
                    if (!isThisExactCell) {
                      return { rowIdx: rIdx + 1, type: "Report" };
                    }
                  }
                  // Check reportMetadataFile
                  if (r.reportMetadataFile && r.reportMetadataFile.name === pdf.name) {
                    const isThisExactCell = (r.rowId === row.rowId && def.field === "reportMetadataFile");
                    if (!isThisExactCell) {
                      return { rowIdx: rIdx + 1, type: "Metadata" };
                    }
                  }
                  return null;
                }, null);

                return (
                  <Option
                    key={pdf.name}
                    value={pdf.name}
                    disabled={Boolean(assignedTarget)}
                    style={{ whiteSpace: "normal", wordBreak: "break-all", padding: "6px 12px" }}
                  >
                    {pdf.name}
                    {assignedTarget
                      ? ` — [Row ${assignedTarget.rowIdx} (${assignedTarget.type})]`
                      : ""}
                  </Option>
                );
              })}
            </Select>
          ) : file ? (
            <div style={{ display: "inline-flex", alignItems: "center", maxWidth: "100%" }}>
              <Tooltip title={isPdfField ? `PDF Document: ${file.name}` : `Image Photo: ${file.name}`}>
                <Tag
                  color={isPdfField ? "blue" : "green"}
                  icon={isPdfField ? null : <PictureOutlined />}
                  style={{
                    maxWidth: 130,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    marginRight: 0,
                    fontSize: 11,
                  }}
                >
                  {file.name}
                </Tag>
              </Tooltip>
            </div>
          ) : isPdfField ? (
            <Tooltip title="No PDF attached — drop PDF files above or browse from computer">
              <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
            </Tooltip>
          ) : (
            <Upload {...uploadProps} style={{ width: "100%", display: "block" }}>
              <Button
                size="small"
                icon={<PictureOutlined style={{ color: "#0a8080" }} />}
                style={{
                  fontSize: 11,
                  height: 24,
                  padding: "0 8px",
                  borderRadius: 4,
                  borderColor: "#b5e3e3",
                  background: "#f7fcfc",
                  color: "#0a8080",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                Upload Photo
              </Button>
            </Upload>
          )}
        </div>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          {hasAttached && (
            <Tooltip title="Clear / Remove file">
              <Button
                size="small"
                type="link"
                danger
                icon={<DeleteOutlined />}
                style={{ padding: 0, fontSize: 12 }}
                onClick={handleClearFile}
              />
            </Tooltip>
          )}
          {isPdfField && (
            <Upload {...uploadProps}>
              <Tooltip title="Browse PDF document">
                <Button size="small" type="link" icon={<UploadOutlined />} style={{ padding: 0 }} />
              </Tooltip>
            </Upload>
          )}
        </div>
      </div>
    );
  }

  if (editing) {
    const stop = () => setEditing(false);

    if (def.type === FIELD_TYPE.MASTER || def.type === FIELD_TYPE.SELECT) {
      const options = optionsForField(def, row, masterData);
      return (
        <Select
          size="small"
          autoFocus
          defaultOpen
          showSearch
          allowClear
          optionFilterProp="children"
          style={{ width: "100%" }}
          value={value || undefined}
          placeholder={`Select ${def.label}`}
          onBlur={stop}
          onChange={(next) => { onChange(next === undefined ? "" : next); stop(); }}
          notFoundContent={emptyListMessage(def, row, options, masterData)}
        >
          {options.map((option) => <Option key={option} value={option}>{option}</Option>)}
        </Select>
      );
    }

    if (def.type === FIELD_TYPE.DATE) {
      // Bounded by whatever stages this row already has filled in, so an
      // out-of-order date is simply not selectable — same restriction the
      // single-order form applies, rather than letting it be picked and
      // bounced by the server afterwards.
      const { min, max } = dateBoundsForField(def.field, row);
      return (
        <DatePicker
          size="small"
          autoFocus
          open
          allowClear
          style={{ width: "100%" }}
          value={value ? moment(value) : null}
          disabledDate={(current) => {
            if (!current) return false;
            if (min && current.isBefore(moment(min), "day")) return true;
            if (max && current.isAfter(moment(max), "day")) return true;
            return false;
          }}
          onChange={(date) => { onChange(date ? date.format("YYYY-MM-DD") : ""); stop(); }}
          onOpenChange={(open) => { if (!open) stop(); }}
          renderExtraFooter={() => (
            <div style={{ padding: "4px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button size="small" type="link" danger style={{ padding: 0 }} onClick={() => { onChange(""); stop(); }}>
                Clear Date
              </Button>
              <Button size="small" type="link" style={{ padding: 0 }} onClick={stop}>
                Close
              </Button>
            </div>
          )}
        />
      );
    }

    return (
      <Input
        size="small"
        autoFocus
        type={def.type === FIELD_TYPE.NUMBER ? "number" : "text"}
        defaultValue={value}
        onBlur={(e) => { onChange(e.target.value); stop(); }}
        onPressEnter={(e) => { onChange(e.target.value); stop(); }}
      />
    );
  }

  const icon = def.type === FIELD_TYPE.MASTER || def.type === FIELD_TYPE.SELECT
    ? <DownOutlined style={{ fontSize: 10, color: "#bfbfbf" }} />
    : def.type === FIELD_TYPE.DATE
      ? <CalendarOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />
      : <EditOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />;

  const clearIcon = value && (def.type === FIELD_TYPE.DATE || def.type === FIELD_TYPE.TEXT || def.type === FIELD_TYPE.NUMBER) ? (
    <Tooltip title={`Clear ${def.label}`}>
      <CloseCircleFilled
        style={{ fontSize: 11, color: "#bfbfbf", cursor: "pointer", marginLeft: 4 }}
        onClick={(e) => {
          e.stopPropagation();
          onChange("");
        }}
      />
    </Tooltip>
  ) : null;

  const display = (
    <div id={cellDomId(row.rowId, def.field)} style={wrapperStyle} onClick={() => setEditing(true)}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
        {value || <Text type="secondary" style={{ fontSize: 12 }}>—</Text>}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
        {clearIcon}
        {icon}
      </span>
    </div>
  );

  if (hasError) {
    return (
      <Tooltip title={directErrors.map((e) => e.message).join(" · ")}>
        {display}
      </Tooltip>
    );
  }

  if (dirty) {
    return (
      <Tooltip title="Edited — press Re-validate to re-check">
        {display}
      </Tooltip>
    );
  }
  return display;
}

export default function ReviewStep({ rows, onChangeRows, masterData, pdfFiles, errorsByCell, dirtyCells, rowStatus, focusedCell }) {
  // Clicking an error in the list below points here — with ~50 columns the
  // offending cell is almost always scrolled out of view, so jumping to it is
  // the difference between a useful error list and one you have to hunt with.
  useEffect(() => {
    if (!focusedCell) return;
    const element = document.getElementById(cellDomId(focusedCell.rowId, focusedCell.field));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [focusedCell]);

  const updateCell = (rowId, field, value) => {
    // Every control's onChange can fire on its way out even when nothing was
    // actually typed or picked — a text input fires onBlur unconditionally,
    // so opening the editor and clicking away without typing looks the same
    // to it as an edit. Only a real value change should count as "dirty".
    let changedFields = null;
    const nextRows = rows.map((row) => {
      if (row.rowId !== rowId) return row;
      if ((row[field] || "") === (value || "")) return row;
      const next = { ...row, [field]: value };
      const touched = [field];
      // Clearing what a changed parent scoped keeps the row from silently
      // holding a combination the server already rejected once.
      (DOWNSTREAM_FIELDS[field] || []).forEach((downstream) => {
        if (next[downstream]) {
          next[downstream] = "";
          touched.push(downstream);
        }
      });
      // Stage dates order validation:
      const stranded = datesInvalidatedBy(field, value, next, isBeforeDay);
      stranded.forEach((key) => {
        next[key] = "";
        touched.push(key);
      });
      if (stranded.length) {
        const rowNumber = rows.findIndex((r) => r.rowId === rowId) + 1;
        message.warning(
          `Row ${rowNumber}: cleared ${stranded.length} date${stranded.length > 1 ? "s" : ""} that are now out of order — please re-enter them.`
        );
      }

      changedFields = touched;
      return next;
    });
    if (!changedFields) return;
    onChangeRows(nextRows, { rowId, fields: changedFields });
  };

  // Master data carries the server's own required-field list; requiredFieldSet
  // falls back to the local copy when it doesn't.
  const requiredSet = useMemo(() => requiredFieldSet(masterData), [masterData]);

  const columns = [
    {
      title: "#",
      key: "index",
      fixed: "left",
      width: 48,
      render: (_, __, index) => <Text type="secondary" style={{ fontSize: 12 }}>{index + 1}</Text>,
    },
    {
      title: "Status",
      key: "status",
      fixed: "left",
      width: 70,
      render: (_, row) => {
        const status = rowStatus ? rowStatus[row.rowId] : null;
        if (!status || status.count === undefined) {
          return <Tooltip title="Not validated yet"><Text type="secondary">—</Text></Tooltip>;
        }
        if (status.count === 0) {
          return (
            <Tooltip title="No errors">
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <CheckCircleTwoTone twoToneColor="#52C41A" style={{ fontSize: 16 }} />
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={`${status.count} error${status.count > 1 ? "s" : ""}`}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "center", width: "100%" }}>
              <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 16 }} />
              <Text type="danger" style={{ fontSize: 12, lineHeight: 1 }}>{status.count}</Text>
            </span>
          </Tooltip>
        );
      },
    },
    ...FIELD_DEFS.map((def) => ({
      title: <ColumnHeader def={def} requirement={requirementFor(def.field, requiredSet)} />,
      key: def.field,
      width: def.width,
      render: (_, row) => (
        <GridCell
          def={def}
          row={row}
          rows={rows}
          masterData={masterData}
          pdfFiles={pdfFiles}
          errors={errorsByCell ? errorsByCell[cellKey(row.rowId, def.field)] : null}
          dirty={dirtyCells ? dirtyCells.has(cellKey(row.rowId, def.field)) : false}
          focused={!!focusedCell && focusedCell.rowId === row.rowId && focusedCell.field === def.field}
          onChange={(value) => updateCell(row.rowId, def.field, value)}
        />
      ),
    })),
  ];

  const totalWidth = FIELD_DEFS.reduce((sum, def) => sum + def.width, 120);

  // Counted from the same source the headers are marked from, so the legend
  // can't claim a number the header row doesn't back up.
  const levelCounts = FIELD_DEFS.reduce((counts, def) => {
    const { level } = requirementFor(def.field, requiredSet);
    counts[level] = (counts[level] || 0) + 1;
    return counts;
  }, {});

  return (
    <>
      <Text type="secondary">
        Every column is editable here — click a cell to change it. A caret means the value comes
        from a list, a pencil means it's typed by hand. Cells the server flagged are outlined in
        red; editing one turns it amber until Re-validate confirms the fix. Click any error in the
        list below to jump straight to its cell.
      </Text>
      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, fontSize: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Column headers:</Text>
        <span>
          <span style={{ color: REQUIRED_MARK, fontWeight: 600 }}>*</span>
          <Text style={{ fontSize: 12, marginLeft: 4 }}>
            required — {levelCounts[REQUIREMENT.REQUIRED] || 0} column
            {(levelCounts[REQUIREMENT.REQUIRED] || 0) === 1 ? "" : "s"}
          </Text>
        </span>
        {!!levelCounts[REQUIREMENT.CONDITIONAL] && (
          <span>
            <span style={{ color: CONDITIONAL_MARK, fontWeight: 600 }}>*</span>
            <Text style={{ fontSize: 12, marginLeft: 4 }}>required only in some cases — hover the header</Text>
          </span>
        )}
        <span>
          <Text style={{ fontSize: 12, color: OPTIONAL_LABEL }}>Greyed</Text>
          <Text style={{ fontSize: 12, marginLeft: 4 }}>
            optional — {levelCounts[REQUIREMENT.OPTIONAL] || 0} column
            {(levelCounts[REQUIREMENT.OPTIONAL] || 0) === 1 ? "" : "s"}, may be left blank
          </Text>
        </span>
      </div>
      <style>{`
        .hob-review-scroll .ant-table-cell { padding: 4px 6px !important; }
        .hob-review-scroll .ant-table-thead > tr > th { font-size: 12px; padding: 6px !important; white-space: nowrap; }
        .hob-review-scroll .ant-table-content, .hob-review-scroll .ant-table-body { scrollbar-width: thin; scrollbar-color: rgba(10, 128, 128, 0.35) transparent; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar, .hob-review-scroll .ant-table-body::-webkit-scrollbar { height: 8px; width: 8px; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar-thumb, .hob-review-scroll .ant-table-body::-webkit-scrollbar-thumb { background: rgba(10, 128, 128, 0.35); border-radius: 4px; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar-track, .hob-review-scroll .ant-table-body::-webkit-scrollbar-track { background: transparent; }
      `}</style>
      <Table
        className="hob-review-scroll"
        style={{ marginTop: 12 }}
        size="small"
        rowKey="rowId"
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ x: totalWidth, y: 300 }}
      />
    </>
  );
}
